import { ErrorResponse } from '@ocean/api'
import { localization } from '@ocean/configs'
import { DateUtils } from '@ocean/utils'
import { ChangePasswordErrorCauses, ChangePasswordErrorDetails } from 'types/changePassword'

const { i18n } = localization

export type ErrorMessageOptions = {
  code: string
  message?: string
  details?: any
}

const translationNS: string = 'errors'
const commonDefaultErrorKey = `${translationNS}.commonDefaultError`

export const CUSTOM_ERROR_CODES = [
  'WindowsServiceIsNotAccessibleResponse',
  // TODO: Maybe better to add customErrorHandler callback to the ApiAction but it will be work for one request only
  // From SimulatorTraining module
  'AddSimulatorTrainingScheduleFirstInstructorMustHaveEmailAssignedResponse',
  'SimulatorTrainingVirtualEventInvalidDurationResponse',
  'VirtualEventInvalidScheduleDurationResponse',
]

class ErrorUtils {
  getErrorMessage(error: ErrorMessageOptions): string {
    const { code, message, details } = error
    let errorKey = `${translationNS}.${code}`

    if (details) {
      const cause = details?.errorCause
      const isSpecialErrorCode = CUSTOM_ERROR_CODES.includes(code)

      if (isSpecialErrorCode) {
        return this.getSpecialErrorMessage(code, details, message)
      }

      if (cause) {
        errorKey += cause
      }
    }

    if (i18n.exists(errorKey)) return i18n.t(errorKey)

    return message || i18n.t(commonDefaultErrorKey)
  }

  getSpecialErrorMessage(code: string, details: any, message?: string): string {
    const errorKey = `${translationNS}.${code}`
    const defaultErrorMessage = i18n.t(commonDefaultErrorKey)

    if (i18n.exists(errorKey)) {
      switch (code) {
        case 'WindowsServiceIsNotAccessibleResponse':
          return i18n.t(errorKey, { serviceName: details })
        case 'SimulatorTrainingVirtualEventInvalidDurationResponse':
        case 'VirtualEventInvalidScheduleDurationResponse':
          const { minDuration, maxDuration } = details
          return i18n.t(errorKey, {
            minDuration: DateUtils.formatNormalizedDuration(minDuration),
            maxDuration: DateUtils.formatNormalizedDuration(maxDuration),
          })
        default:
          return defaultErrorMessage
      }
    }

    switch (code) {
      case 'AddSimulatorTrainingScheduleFirstInstructorMustHaveEmailAssignedResponse':
        if (!message) return defaultErrorMessage
        const { userId, userName } = details
        return message.replaceAll(userId, userName)
      default:
        return defaultErrorMessage
    }
  }

  getPasswordErrorMessage(error: ErrorResponse, localizationNamespace: string): string {
    if (!error?.details || !localizationNamespace) return ''

    let errorMessage = ''

    const errorDetails = error.details as ChangePasswordErrorDetails
    const errorCauses = errorDetails.errorCausesList

    const getMessage = (name: string, details?: any) =>
      i18n.t(`${localizationNamespace}.${name}`, details)

    for (const item of errorCauses) {
      switch (item) {
        case ChangePasswordErrorCauses.EmptyPassword:
          errorMessage += getMessage('emptyPassword')
          break
        case ChangePasswordErrorCauses.InvalidCurrentPassword:
          errorMessage += getMessage('invalidCurrentPassword')
          break
        case ChangePasswordErrorCauses.PasswordDoesNotMeetPatternRequirements:
          errorMessage += getMessage('passwordMustContain')
          const requiredCharacters = []
          if (errorDetails.passwordRequirements.requiredLetterCharactersNumber > 0) {
            requiredCharacters.push(getMessage('letters'))
          }
          if (errorDetails.passwordRequirements.requiredDigitCharactersNumber > 0) {
            requiredCharacters.push(getMessage('digits'))
          }
          if (errorDetails.passwordRequirements.requiredSpecialCharactersNumber > 0) {
            requiredCharacters.push(getMessage('specialCharacters'))
          }
          errorMessage += requiredCharacters.join(', ')
          errorMessage += '.'
          break
        case ChangePasswordErrorCauses.PasswordIsDateOfBirthBased:
          errorMessage += getMessage('passwordIsDateOfBirthBased')
          break
        case ChangePasswordErrorCauses.PasswordStartsOrEndsWithWhitespaces:
          errorMessage += getMessage('passwordStartsOrEndsWithWhitespaces')
          break
        case ChangePasswordErrorCauses.PasswordTheSameAsCurrentOne:
          errorMessage += getMessage('passwordTheSameAsCurrentOne')
          break
        case ChangePasswordErrorCauses.PasswordTooShort:
          errorMessage += getMessage('passwordTooShort', errorDetails.passwordRequirements)
          break
        case ChangePasswordErrorCauses.PasswordWasUsedBefore:
          errorMessage += getMessage('passwordWasUsedBefore')
          break
      }
      errorMessage += ' '
    }

    return errorMessage
  }
}

export default new ErrorUtils()
